import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

// Inline SVG placeholder — no file, no network request, no loop
const PLACEHOLDER_IMAGE =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="160" height="160">' +
    '<rect width="160" height="160" fill="#f3f4f6"/>' +
    '<text x="80" y="80" font-family="sans-serif" font-size="12" fill="#9ca3af" text-anchor="middle" dominant-baseline="middle">No image</text>' +
    '</svg>'
  );

export default function MemberDraftProductCard({
  product,
  isInDraft,
  onAdd,
  onRemove,
  onUpdateQuantity,
  submitting,
  readOnly = false,
  quantity: initialQuantity = 0
}) {
  const [localQuantity, setLocalQuantity] = useState(initialQuantity);
  const [isUpdating, setIsUpdating] = useState(false);
  const debounceTimer = useRef(null);

  // Update local quantity when prop changes (from parent refresh)
  useEffect(() => {
    setLocalQuantity(initialQuantity);
  }, [initialQuantity]);

  // Format price
  const formattedPrice = product.price?.toFixed(2) || '0.00';

  // Calculate the actual price based on quantity (volume pricing)
  const getPriceForQuantity = (qty) => {
    if (!product.volumePricing || product.volumePricing.length === 0) {
      return product.price;
    }
    // Sort tiers by minQuantity (ascending)
    const sortedTiers = [...product.volumePricing].sort((a, b) => a.minQuantity - b.minQuantity);
    let bestPrice = product.price;
    for (const tier of sortedTiers) {
      if (qty >= tier.minQuantity) {
        bestPrice = tier.price;
      }
    }
    return bestPrice;
  };

  // Calculate current price based on local quantity
  const currentPrice = getPriceForQuantity(localQuantity);
  const totalPrice = currentPrice * localQuantity;
  const listPriceTotal = product.price * localQuantity;
  const savingsAmount = listPriceTotal - totalPrice;

  // Get the next tier (if any)
  const getNextTier = (qty) => {
    if (!product.volumePricing || product.volumePricing.length === 0) {
      return null;
    }
    const sortedTiers = [...product.volumePricing].sort((a, b) => a.minQuantity - b.minQuantity);
    for (const tier of sortedTiers) {
      if (qty < tier.minQuantity) {
        return tier;
      }
    }
    return null;
  };

  const nextTier = getNextTier(localQuantity);

  // Get image URL
const rawImage = product.images && product.images[0];
const imageUrl = rawImage
  ? (rawImage.startsWith('http') ? rawImage : `/uploads/${rawImage}`)
    : PLACEHOLDER_IMAGE;

  // Get vendor name
  let vendorText = '';
  let vendorSlug = '';
  if (product.producer) {
    vendorText = product.producer.farmName || product.producer.name;
    vendorSlug = product.producer.slug;
  } else if (product.hubId) {
    vendorText = product.hubId.name || 'Hub';
    vendorSlug = product.hubId.slug;
  }

  let vendorLink = '#';
  if (product.producer) {
    vendorLink = `/producer/${vendorSlug || product.producer._id}`;
  } else if (product.hubId) {
    vendorLink = `/hub/${vendorSlug || product.hubId._id}`;
  }

  // Distance badge color
  const getDistanceColor = (band) => {
    const colors = {
      very_close: 'bg-green-100 text-green-800',
      close: 'bg-blue-100 text-blue-800',
      moderate: 'bg-yellow-100 text-yellow-800',
      far: 'bg-orange-100 text-orange-800',
      very_far: 'bg-red-100 text-red-800',
      unknown: 'bg-gray-100 text-gray-600'
    };
    return colors[band] || colors.unknown;
  };

  // Debounced update to server
  const debouncedUpdate = (newQuantity) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    if (newQuantity === 0 && isInDraft) {
      debounceTimer.current = setTimeout(() => {
        if (onRemove) onRemove(product._id);
        setIsUpdating(false);
      }, 400);
      return;
    }
    
    if (newQuantity > 0 && isInDraft) {
      debounceTimer.current = setTimeout(() => {
        if (onUpdateQuantity) {
          onUpdateQuantity(product._id, newQuantity);
        }
        setIsUpdating(false);
      }, 400);
      return;
    }
    
    if (newQuantity > 0 && !isInDraft) {
      debounceTimer.current = setTimeout(() => {
        if (onAdd) onAdd(product._id, newQuantity);
        setIsUpdating(false);
      }, 400);
    }
  };

  const handleDecrement = () => {
    if (localQuantity > 0) {
      const newQty = localQuantity - 1;
      setLocalQuantity(newQty);
      setIsUpdating(true);
      
      if (newQty === 0 && isInDraft) {
        debouncedUpdate(0);
      } else if (newQty > 0) {
        debouncedUpdate(newQty);
      } else {
        setIsUpdating(false);
      }
    }
  };

  const handleIncrement = () => {
    const newQty = localQuantity + 1;
    setLocalQuantity(newQty);
    setIsUpdating(true);
    debouncedUpdate(newQty);
  };

  const handleAdd = () => {
    const newQty = 1;
    setLocalQuantity(newQty);
    setIsUpdating(true);
    // Call onAdd immediately and let the card update
    if (onAdd) {
      onAdd(product._id, newQty);
      // The server response will update the draft, but we've already updated locally
      setTimeout(() => {
        setIsUpdating(false);
      }, 500);
    }
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow border border-gray-100 h-full flex flex-col">
      <Link
        to={`/product/${product._id}`}
        className="block w-full h-40 bg-gray-50 hover:opacity-90 transition-opacity flex-shrink-0"
      >
        <img
          src={imageUrl}
          alt={product.name || 'Product'}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = PLACEHOLDER_IMAGE;
          }}
        />
      </Link>

      <div className="flex-1 p-3 flex flex-col">
        <div className="flex items-start justify-between gap-1">
          <Link
            to={`/product/${product._id}`}
            className="flex-1 hover:text-green-700 transition-colors"
          >
            <h4 className="text-sm font-semibold text-gray-800 hover:underline line-clamp-1">
              {product.name || 'Unnamed Product'}
            </h4>
          </Link>
          <div className="flex items-center gap-0.5 flex-shrink-0 flex-wrap">
            {product.organic && (
              <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">
                Organic
              </span>
            )}
          </div>
        </div>

        {product.description && (
          <p className="text-xs text-gray-500 mt-1 line-clamp-2 flex-1">
            {product.description}
          </p>
        )}

        {/* Volume pricing tier indicator - only show if there's a next tier and quantity is below it */}
        {nextTier && localQuantity > 0 && localQuantity < nextTier.minQuantity && (
          <div className="mt-1 text-xs text-purple-600">
            💰 Add {nextTier.minQuantity - localQuantity} more for ${nextTier.price.toFixed(2)} each
          </div>
        )}
        
        {/* Show current discount if volume pricing is active */}
        {localQuantity > 0 && savingsAmount > 0 && (
          <div className="mt-1 text-xs text-green-600">
            💰 Saving ${savingsAmount.toFixed(2)} with volume pricing
          </div>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-gray-500">
          {product.source !== 'vhub' && (
            <span className="flex items-center gap-0.5">
              <span className="font-medium">From:</span>
              <Link
                to={vendorLink}
                className="text-blue-600 hover:text-blue-800 hover:underline truncate max-w-[80px] sm:max-w-[120px]"
              >
                {vendorText || 'Unknown'}
              </Link>
            </span>
          )}

          {product.distance !== null && product.distance !== undefined && (
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${getDistanceColor(product.distanceBand)}`}>
              {product.distance} km
            </span>
          )}
        </div>

        <div className="mt-2 flex items-center justify-between border-t pt-2">
          <div>
            <span className="text-lg font-bold text-green-600">${currentPrice.toFixed(2)}</span>
            <span className="text-xs text-gray-400 ml-0.5">/{product.unit || 'each'}</span>
            {localQuantity > 0 && (
              <span className="text-xs text-gray-500 ml-1">
                × {localQuantity} = ${totalPrice.toFixed(2)}
                {product.price !== currentPrice && (
                  <span className="text-green-600 ml-1">(was ${(product.price * localQuantity).toFixed(2)})</span>
                )}
              </span>
            )}
            {product.stats?.avg30d && product.stats.avg30d > product.price && (
              <div className="text-[10px] text-green-700 font-medium mt-0.5">
                save ${(product.stats.avg30d - product.price).toFixed(2)} ({Math.round(((product.stats.avg30d - product.price) / product.stats.avg30d) * 100)}% below avg)
              </div>
            )}
          </div>

          {!readOnly ? (
            <div className="flex items-center gap-1">
              <button
                onClick={handleDecrement}
                disabled={submitting || localQuantity === 0 || isUpdating}
                className={`w-7 h-7 rounded-full border flex items-center justify-center text-sm font-bold transition-colors ${
                  localQuantity === 0 || isUpdating
                    ? 'border-gray-200 text-gray-300 cursor-not-allowed'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                }`}
                aria-label="Decrease quantity"
              >
                −
              </button>
              
              <span className="w-6 text-center text-sm font-medium text-gray-700">
                {isUpdating ? '...' : localQuantity}
              </span>
              
              <button
                onClick={handleIncrement}
                disabled={submitting || isUpdating}
                className={`w-7 h-7 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-100 flex items-center justify-center text-sm font-bold transition-colors ${
                  isUpdating ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                aria-label="Increase quantity"
              >
                +
              </button>
              
              {localQuantity === 0 && (
                <button
                  onClick={handleAdd}
                  disabled={submitting || product.quantity === 0}
                  className={`ml-1 px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                    product.quantity === 0
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  Add
                </button>
              )}
            </div>
          ) : (
            <span className="text-xs text-gray-400">
              {isInDraft ? `✅ ${localQuantity} in draft` : ''}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
